<template>
    <div>
        <div v-if="debugMenuVisibility" class="map-debug-menu" :class="{ 'is-holding': debugOrientation.isHolding }">
            <div class="header" :class="{ 'hold-element': debugOrientation.isHolding }">
                <div class="title">Debug Menu</div>
                <b-btn variant="text" class="close" @click="debugMenuVisibility = false">
                    <i class="fas fa-times" />
                </b-btn>
            </div>

            <div class="options" :class="{ 'hold-element': debugOrientation.isHolding }">
                <b-checkbox variant="info" v-model="freeNavigation">Free Navigation</b-checkbox>
                <b-checkbox variant="info" v-model="emulateGPS" @change="$emit(`emulate-gps-change`)">Emulate GPS</b-checkbox>
                <b-checkbox variant="info" v-if="emulateGPS" v-model="noisyGPS" @change="$emit(`toggle-noisy-signal`)">Enable Noisy signal</b-checkbox>
                <b-checkbox variant="info" v-model="debugPedometerWithoutGps" @change="$emit(`debug-pedometer-without-gps-change`, $event)">Enable pedometer without GPS</b-checkbox>
                <b-checkbox variant="info" v-model="disableOrientation">Disable Orientation</b-checkbox>
                <b-checkbox variant="info" v-model="showArPanoGridInLive">Show AR pano grid in live</b-checkbox>
                <b-btn variant="simple" size="sm" @click="$emit(`load-debug-config`)">Load debug config</b-btn>
            </div>

            <div class="actions">
                <b-button :class="{ 'hold-element': debugOrientation.isHolding }" variant="simple" size="lg"
                    @click="openConfig">
                    Modify Config
                </b-button>
                <e-joystick v-if="gpsLocationMarkerItem" :size="82" :stick-size="30" :min-interval="70" :max-interval="600"
                    emit-axis="all"
                    @change="$emit(`step-joystick-change`, $event)"
                    @emit="$emit(`simulate-step-from-joystick`, $event)"
                    @stick-hold-start="$emit(`stick-hold-start`)"
                    @stick-hold-end="$emit(`stick-hold-end`)" />
                <b-button :class="{ 'hold-element': debugOrientation.isHolding }" variant="simple" size="lg" @click="debugInfoModalVisible = true">
                    Show Info
                </b-button>
            </div>
        </div>

        <e-mobile-modal v-model="debugInfoModalVisible" fullscreen position="center" title="Sensors Status" dialog-class="map-debug-dialog" panel-class="map-debug-dialog-panel">
            <div class="debug-modal debug-info-modal">
                <div class="summary-grid">
                    <div class="summary-card">
                        <div class="label">Accelerometer</div>
                        <div class="value">{{ sensorsInitalStates.accelerometer }}</div>
                    </div>
                    <div class="summary-card">
                        <div class="label">Geolocation</div>
                        <div class="value">{{ sensorsInitalStates.geolocation }}</div>
                    </div>
                    <div class="summary-card">
                        <div class="label">On Site</div>
                        <div class="value">{{ formatBoolean(isCurrentLocationOnSite) }}</div>
                    </div>
                    <div class="summary-card">
                        <div class="label">Camera Follow</div>
                        <div class="value">{{ formatBoolean(cameraFollowMarker) }}</div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Accelerometer</div>
                    <div class="rows">
                        <div class="row"><span class="name">X</span><span class="value">{{ formatValue(sensors.accelerometer && sensors.accelerometer.x) }}</span></div>
                        <div class="row"><span class="name">Y</span><span class="value">{{ formatValue(sensors.accelerometer && sensors.accelerometer.y) }}</span></div>
                        <div class="row"><span class="name">Z</span><span class="value">{{ formatValue(sensors.accelerometer && sensors.accelerometer.z) }}</span></div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Gyroscope</div>
                    <div class="rows">
                        <div class="row"><span class="name">Alpha</span><span class="value">{{ formatValue(sensors.gyroscope && sensors.gyroscope.alpha) }}</span></div>
                        <div class="row"><span class="name">Beta</span><span class="value">{{ formatValue(sensors.gyroscope && sensors.gyroscope.beta) }}</span></div>
                        <div class="row"><span class="name">Gamma</span><span class="value">{{ formatValue(sensors.gyroscope && sensors.gyroscope.gamma) }}</span></div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Geolocation</div>
                    <div class="rows">
                        <div class="row"><span class="name">Accuracy</span><span class="value">{{ formatValue(sensors.geolocation && sensors.geolocation.coords.accuracy) }}</span></div>
                        <div class="row"><span class="name">Timestamp</span><span class="value">{{ formatValue(sensors.geolocation && sensors.geolocation.timestamp) }}</span></div>
                        <div class="row"><span class="name">Latitude</span><span class="value">{{ formatValue(sensors.geolocation && sensors.geolocation.coords.latitude) }}</span></div>
                        <div class="row"><span class="name">Longitude</span><span class="value">{{ formatValue(sensors.geolocation && sensors.geolocation.coords.longitude) }}</span></div>
                        <div class="row"><span class="name">Altitude</span><span class="value">{{ formatValue(sensors.geolocation && sensors.geolocation.coords.altitude) }}</span></div>
                        <div class="row"><span class="name">Altitude Accuracy</span><span class="value">{{ formatValue(sensors.geolocation && sensors.geolocation.coords.altitudeAccuracy) }}</span></div>
                        <div class="row"><span class="name">Heading</span><span class="value">{{ formatValue(sensors.geolocation && sensors.geolocation.coords.heading) }}</span></div>
                        <div class="row"><span class="name">Speed</span><span class="value">{{ formatValue(sensors.geolocation && sensors.geolocation.coords.speed) }}</span></div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Compass</div>
                    <div class="rows">
                        <div class="row"><span class="name">Heading</span><span class="value">{{ formatValue(sensors.compass && sensors.compass.heading) }}</span></div>
                        <div class="row"><span class="name">True Heading</span><span class="value">{{ formatValue(sensors.compass && sensors.compass.trueHeading) }}</span></div>
                        <div class="row"><span class="name">Raw Heading</span><span class="value">{{ formatValue(sensors.compass && sensors.compass.rawHeading) }}</span></div>
                        <div class="row"><span class="name">Declination</span><span class="value">{{ formatValue(sensors.compass && sensors.compass.declination) }}</span></div>
                        <div class="row"><span class="name">Accuracy</span><span class="value">{{ formatValue(sensors.compass && sensors.compass.accuracy) }}</span></div>
                        <div class="row"><span class="name">Alpha</span><span class="value">{{ formatValue(sensors.compass && sensors.compass.alpha) }}</span></div>
                        <div class="row"><span class="name">Beta</span><span class="value">{{ formatValue(sensors.compass && sensors.compass.beta) }}</span></div>
                        <div class="row"><span class="name">Gamma</span><span class="value">{{ formatValue(sensors.compass && sensors.compass.gamma) }}</span></div>
                        <div class="row"><span class="name">Absolute</span><span class="value">{{ formatBoolean(sensors.compass && sensors.compass.absolute) }}</span></div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">State</div>
                    <div class="rows">
                        <div class="row"><span class="name">Should ask for motion permission</span><span class="value">{{ formatBoolean(sensors.shouldAskForMotionPermission) }}</span></div>
                        <div class="row"><span class="name">Should ask for geolocation permission</span><span class="value">{{ formatBoolean(sensors.shouldAskForGeolocationPermission) }}</span></div>
                        <div class="row"><span class="name">Motion listener registered</span><span class="value">{{ formatBoolean(sensors.motionListenerRegistered) }}</span></div>
                    </div>
                </div>
            </div>

            <template #footer>
                <b-btn size="lg" variant="light" block @click="debugInfoModalVisible = false">
                    Close
                </b-btn>
            </template>
        </e-mobile-modal>

        <e-mobile-modal v-model="stepFilterConfigModalVisible" fullscreen position="center" title="Sensors Configuration" dialog-class="map-debug-dialog" panel-class="map-debug-dialog-panel">
            <div class="debug-modal debug-config-modal">
                <div class="section">
                    <div class="section-title">Geolocation Thresholds</div>
                    <div class="field-grid">
                        <label class="field">
                            <span class="name">Excellent</span>
                            <span class="hint">Max accuracy in meters</span>
                            <b-input type="number" v-model.number="sensors.thresholds.geolocation.excellent" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Good</span>
                            <span class="hint">Max accuracy in meters</span>
                            <b-input type="number" v-model.number="sensors.thresholds.geolocation.good" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Medium</span>
                            <span class="hint">Max accuracy in meters</span>
                            <b-input type="number" v-model.number="sensors.thresholds.geolocation.medium" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Low</span>
                            <span class="hint">Max accuracy in meters</span>
                            <b-input type="number" v-model.number="sensors.thresholds.geolocation.low" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Off-route Accuracy Factor</span>
                            <span class="hint">Multiplier for adaptive off-route threshold</span>
                            <b-input type="number" v-model.number="offRouteAccuracyFactorValue" min="0" max="5" step="0.05" />
                        </label>
                        <label class="field">
                            <span class="name">Previous Altitude</span>
                            <span class="hint">Previous floor detection sample</span>
                            <b-input type="number" v-model.number="prevAltitudeValue" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Fake Accuracy</span>
                            <span class="hint">Fallback GPS accuracy</span>
                            <b-input type="number" v-model.number="fakeAccuracyValue" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Instruction Distance Threshold</span>
                            <span class="hint">Meters before the next instruction</span>
                            <b-input type="number" v-model.number="instructionDistanceThresholdValue" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Off Route Threshold Meters</span>
                            <span class="hint">Base off-route trigger</span>
                            <b-input type="number" v-model.number="offRouteThresholdMetersValue" min="0" step="1" />
                        </label>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Compass Thresholds</div>
                    <div class="field-grid">
                        <label class="field">
                            <span class="name">Excellent</span>
                            <span class="hint">Max accuracy in degrees</span>
                            <b-input type="number" v-model.number="sensors.thresholds.compass.excellent" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Good</span>
                            <span class="hint">Max accuracy in degrees</span>
                            <b-input type="number" v-model.number="sensors.thresholds.compass.good" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Medium</span>
                            <span class="hint">Max accuracy in degrees</span>
                            <b-input type="number" v-model.number="sensors.thresholds.compass.medium" min="1" step="1" />
                        </label>
                        <label class="field">
                            <span class="name">Low</span>
                            <span class="hint">Max accuracy in degrees</span>
                            <b-input type="number" v-model.number="sensors.thresholds.compass.low" min="1" step="1" />
                        </label>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Live Status</div>
                    <div class="summary-grid">
                        <div class="summary-card">
                            <div class="label">Step Count</div>
                            <div class="value">{{ pedometer.stepCount }}</div>
                        </div>
                        <div class="summary-card">
                            <div class="label">Distance</div>
                            <div class="value">{{ pedometer.distanceFormatted }}</div>
                        </div>
                        <div class="summary-card">
                            <div class="label">Stride Length</div>
                            <div class="value">{{ pedometer.strideLength.toFixed(3) }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <template #footer>
                <b-btn size="lg" variant="light" block @click="stepFilterConfigModalVisible = false">
                    Close
                </b-btn>
                <b-btn size="lg" variant="accent" block @click="saveConfig">
                    Save
                </b-btn>
            </template>
        </e-mobile-modal>
    </div>
</template>

<script>
import EMobileModal from "@/components/e-mobile-modal.vue";
import EJoystick from "@/components/e-joystick.vue";
import MapMixin from "@/helpers/maps/map-mixin.js";
import { Pedometer } from "@/helpers/maps/Pedometer.js";

export default {
    name: `map-debug-menu`,
    components: {
        EMobileModal,
        EJoystick
    },
    mixins: [MapMixin],
    props: {
        gpsLocationMarkerItem: { type: Object },
        sensorsInitalStates: { type: Object },
        cameraFollowMarker: { type: Boolean },
        sensors: { type: Object },
        pedometerConfig: { type: Object },
        sensitivityOptions: { type: Array },
        pedometer: { type: Pedometer },
        offRouteAccuracyFactor: { type: Number },
        prevAltitude: { type: Number },
        fakeAccuracy: { type: Number },
        instructionDistanceThreshold: { type: Number },
        offRouteThresholdMeters: { type: Number }
    },
    data() {
        return {
            debugInfoModalVisible: false,
            stepFilterConfigModalVisible: false
        };
    },
    computed: {
        offRouteAccuracyFactorValue: {
            get() {
                return this.offRouteAccuracyFactor;
            },
            set(value) {
                this.$emit(`update:off-route-accuracy-factor`, value);
            }
        },
        prevAltitudeValue: {
            get() {
                return this.prevAltitude;
            },
            set(value) {
                this.$emit(`update:prev-altitude`, value);
            }
        },
        fakeAccuracyValue: {
            get() {
                return this.fakeAccuracy;
            },
            set(value) {
                this.$emit(`update:fake-accuracy`, value);
            }
        },
        instructionDistanceThresholdValue: {
            get() {
                return this.instructionDistanceThreshold;
            },
            set(value) {
                this.$emit(`update:instruction-distance-threshold`, value);
            }
        },
        offRouteThresholdMetersValue: {
            get() {
                return this.offRouteThresholdMeters;
            },
            set(value) {
                this.$emit(`update:off-route-threshold-meters`, value);
            }
        }
    },
    methods: {
        openConfig() {
            this.$emit(`sync-pedometer-config`);
            this.stepFilterConfigModalVisible = true;
        },
        saveConfig() {
            this.$emit(`save-debug-config`);
            this.stepFilterConfigModalVisible = false;
        },
        formatValue(value) {
            return value || value === 0 ? value : `-`;
        },
        formatBoolean(value) {
            if (value || value === false) {
                return value ? `Yes` : `No`;
            }
            return `-`;
        }
    }
}
</script>

<style lang="scss" scoped>
.map-debug-menu {
    position: absolute;
    bottom: 80px;
    left: 16px;
    right: 16px;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;

    padding: 12px;

    background-color: rgb(31 31 31 / 70%);
    backdrop-filter: blur(10px);
    color: white;
    border-radius: 12px;
    border: 1px solid #ffffff7a;

    transition: ease-in-out 0.2s;

    &.is-holding {
        background-color: rgba(12, 55, 73, 0) !important;
        backdrop-filter: blur(0px) !important;
        border: none !important;

        .hold-element {
            opacity: 0;
        }
    }

    .header {
        display: flex;
        flex-direction: row;
        align-items: center;
        width: 100%;
        gap: 10px;
    }

    .title {
        font-weight: 500;
        font-size: 14pt;
    }

    .close {
        margin-left: auto;
    }

    button {
        color: white;
        border: 1px solid #ffffff7a;
        border-radius: 6px;
    }

    .options {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
    }

    .actions {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        gap: 10px;
        width: 100%;

        button {
            flex: 1;
        }
    }

    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
        background: var(--color-accent);
    }
}

.debug-modal-header {
    padding-right: 40px;

    .eyebrow {
        font-size: 10pt;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-accent);
    }
}

.debug-modal {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 100%;
    padding-bottom: 12px;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
}

.summary-card,
.section {
    background: linear-gradient(180deg, #ffffff 0%, #f6f8fb 100%);
    border: 1px solid #dfe5ef;
    border-radius: 18px;
    box-shadow: 0 10px 30px rgba(22, 35, 60, 0.06);
}

.summary-card {
    padding: 14px 16px;

    .label {
        font-size: 10pt;
        color: #6d7785;
    }

    .value {
        margin-top: 6px;
        font-size: 16pt;
        font-weight: 600;
        color: var(--color-dark);
    }
}

.section {
    padding: 16px;
}

.section-title {
    margin-bottom: 14px;
    font-size: 13pt;
    font-weight: 600;
    color: var(--color-dark);
}

.rows {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 10px;
    padding-inline: 12px;
    border-bottom: 1px solid #e6ebf3;

    &:last-child {
        padding-bottom: 0;
        border-bottom: none;
    }

    .name {
        font-size: 10.5pt;
        color: #6d7785;
    }

    .value {
        font-size: 11pt;
        font-weight: 600;
        color: var(--color-dark);
        text-align: right;
        word-break: break-word;
    }
}

.field-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.78);
    border: 1px solid #e6ebf3;

    .name {
        font-size: 10.5pt;
        font-weight: 600;
        color: var(--color-dark);
    }

    .hint {
        font-size: 9.5pt;
        color: #7a8594;
    }
}

.debug-config-modal {
    :deep(.form-control),
    :deep(.custom-select) {
        min-height: 44px;
        border-radius: 12px;
        border-color: #d5ddeb;
        box-shadow: none;
    }
}

@media (max-width: 767px) {
    .field-grid,
    .summary-grid {
        grid-template-columns: 1fr;
    }

    .row {
        align-items: flex-start;
        flex-direction: column;
        gap: 4px;

        .value {
            text-align: left;
        }
    }
}
</style>
